import { toast } from '@heroui/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import {
  AI_BILLING_CURRENCY_LABELS,
  AI_BILLING_DIMENSIONS,
  AI_INVOKE_STATUSES,
  AI_INVOKE_STATUS_LABELS,
  type AiBillingCurrency,
} from '../constants';
import {
  AiInvokeUsageItemsEditor,
  EMPTY_USAGE_ITEM,
  type InvokeUsageItemFormValues,
} from './AiInvokeUsageItemsEditor';

const usageItemSchema = z.object({
  usage: z.enum(AI_BILLING_DIMENSIONS),
  quantity: z.coerce.number().min(0, '使用数量不能为负数'),
  unit_price: z.coerce.number().min(0, '单价不能为负数'),
  amount: z.coerce.number().min(0, '小计不能为负数'),
});

const invokeLogSchema = z.object({
  spaceId: z.string().min(1, '请选择空间'),
  userId: z.string().min(1, '请选择用户'),
  providerId: z.string().min(1, '请选择供应商'),
  modelId: z.string().min(1, '请选择模型'),
  ruleId: z.string().min(1, '请选择计费规则'),
  requestId: z.string().min(1, '请求 ID 不能为空').max(100, '请求 ID 长度不能超过100字符'),
  usageItems: z.array(usageItemSchema).min(1, '至少添加一条使用明细'),
  amount: z.coerce.number().min(0, '总计不能为负数'),
  status: z.enum(AI_INVOKE_STATUSES),
});

type InvokeLogFormValues = z.infer<typeof invokeLogSchema>;

export type AiInvokeLogFormItem = {
  id?: string;
  spaceId?: string | null;
  userId?: string | null;
  providerId?: string | null;
  modelId?: string | null;
  ruleId?: string | null;
  requestId?: string | null;
  usageItems?: InvokeUsageItemFormValues[] | null;
  amount?: number | null;
  status?: string | null;
};

interface AiInvokeLogEditorModalProps {
  open: boolean;
  log?: AiInvokeLogFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function normalizeUsageItems(raw: unknown): InvokeUsageItemFormValues[] {
  if (!Array.isArray(raw) || raw.length === 0) return [{ ...EMPTY_USAGE_ITEM }];
  return raw.map((item) => {
    const row = item as Record<string, unknown>;
    const usage = AI_BILLING_DIMENSIONS.includes(row.usage as never)
      ? (row.usage as InvokeUsageItemFormValues['usage'])
      : 'INPUT_TOKEN';
    return {
      usage,
      quantity: Number(row.quantity ?? 0),
      unit_price: Number(row.unit_price ?? row.unitPrice ?? 0),
      amount: Number(row.amount ?? 0),
    };
  });
}

function sumUsageAmounts(items: InvokeUsageItemFormValues[]): number {
  return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
}

export function AiInvokeLogEditorModal({
  open,
  log,
  onCancel,
  onSuccess,
}: AiInvokeLogEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!log;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const { data: spacesData } = trpc.spaces.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated && open }
  );
  const { data: usersData } = trpc.users.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated && open }
  );
  const { data: providersData } = trpc.aiProvider.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated && open }
  );
  const { data: modelsData } = trpc.aiModel.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated && open }
  );
  const { data: rulesData } = trpc.aiBillingRule.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated && open }
  );

  const spaces = useMemo(
    () => (spacesData ?? []) as Array<{ id: string; name?: string | null }>,
    [spacesData]
  );
  const users = useMemo(
    () =>
      (usersData ?? []) as Array<{
        id: string;
        username?: string | null;
        nickname?: string | null;
      }>,
    [usersData]
  );
  const providers = useMemo(
    () =>
      (providersData ?? []) as Array<{
        id: string;
        name?: string | null;
        code?: string | null;
      }>,
    [providersData]
  );
  const models = useMemo(
    () =>
      (modelsData ?? []) as Array<{
        id: string;
        modelName?: string | null;
        modelCode?: string | null;
        modelVersion?: string | null;
      }>,
    [modelsData]
  );
  const rules = useMemo(
    () =>
      (rulesData ?? []) as Array<{
        id: string;
        modelId?: string | null;
        version?: number | null;
        currency?: string | null;
      }>,
    [rulesData]
  );

  const modelLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of models) {
      const name = m.modelName || m.modelCode || m.id;
      const ver = m.modelVersion ? ` (${m.modelVersion})` : '';
      map.set(m.id, `${name}${ver}`);
    }
    return map;
  }, [models]);

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<InvokeLogFormValues>({
    resolver: zodResolver(invokeLogSchema) as any,
    defaultValues: {
      spaceId: '',
      userId: '',
      providerId: '',
      modelId: '',
      ruleId: '',
      requestId: '',
      usageItems: [{ ...EMPTY_USAGE_ITEM }],
      amount: 0,
      status: 'SUCCESS',
    },
    mode: 'onChange',
  });
  const { reset, control, setValue, watch } = form;

  const syncAmountFromItems = useCallback(() => {
    const items = watch('usageItems') ?? [];
    setValue('amount', sumUsageAmounts(items), { shouldValidate: true });
  }, [setValue, watch]);

  const createLogMutation = trpc.aiInvokeLog.create.useMutation({
    onSuccess: () => {
      toast.success('调用日志创建成功');
      form.reset();
      onSuccess();
      void utils.aiInvokeLog.list.invalidate();
      void utils.aiInvokeLog.listAll.invalidate();
    },
  });

  const updateLogMutation = trpc.aiInvokeLog.update.useMutation({
    onSuccess: () => {
      toast.success('调用日志更新成功');
      form.reset();
      onSuccess();
      void utils.aiInvokeLog.list.invalidate();
      void utils.aiInvokeLog.listAll.invalidate();
    },
  });

  // Only sync form when modal opens / edit target changes — avoid reset() while closed
  // (reset triggers re-render; unstable deps previously caused Maximum update depth exceeded).
  useEffect(() => {
    if (!open) return;

    if (isEdit && log) {
      const usageItems = normalizeUsageItems(log.usageItems);
      reset({
        spaceId: log.spaceId || '',
        userId: log.userId || '',
        providerId: log.providerId || '',
        modelId: log.modelId || '',
        ruleId: log.ruleId || '',
        requestId: log.requestId || '',
        usageItems,
        amount: log.amount ?? sumUsageAmounts(usageItems),
        status: (AI_INVOKE_STATUSES as readonly string[]).includes(log.status ?? '')
          ? (log.status as (typeof AI_INVOKE_STATUSES)[number])
          : 'SUCCESS',
      });
      return;
    }

    reset({
      spaceId: spaces[0]?.id ?? '',
      userId: users[0]?.id ?? '',
      providerId: providers[0]?.id ?? '',
      modelId: models[0]?.id ?? '',
      ruleId: rules[0]?.id ?? '',
      requestId: '',
      usageItems: [{ ...EMPTY_USAGE_ITEM }],
      amount: 0,
      status: 'SUCCESS',
    });
  }, [open, isEdit, log, reset, spaces, users, providers, models, rules]);

  const onSubmit = async (values: InvokeLogFormValues) => {
    const total = sumUsageAmounts(values.usageItems);
    const payload = {
      spaceId: values.spaceId,
      userId: values.userId,
      providerId: values.providerId,
      modelId: values.modelId,
      ruleId: values.ruleId,
      requestId: values.requestId,
      usageItems: values.usageItems,
      amount: total,
      status: values.status,
    };

    if (isEdit && log?.id) {
      await updateLogMutation.mutateAsync({ id: log.id, data: payload });
    } else {
      await createLogMutation.mutateAsync(payload);
    }
  };

  const isLoading = createLogMutation.isPending || updateLogMutation.isPending;
  const errors = form.formState.errors;

  const renderSelect = (
    name: keyof Pick<
      InvokeLogFormValues,
      'spaceId' | 'userId' | 'providerId' | 'modelId' | 'ruleId'
    >,
    label: string,
    items: Array<{ id: string; label: string }>
  ) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <Select
          className="w-full"
          isInvalid={!!errors[name]}
          variant="secondary"
          value={field.value || null}
          onChange={(key) => {
            if (key != null) field.onChange(String(key));
          }}
        >
          <Label className="text-sm font-medium">
            {label} <span className="text-danger">*</span>
          </Label>
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {items.map((item) => (
                <ListBox.Item key={item.id} id={item.id} textValue={item.label}>
                  {item.label}
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
          {errors[name] ? <FieldError>{errors[name]?.message}</FieldError> : null}
        </Select>
      )}
    />
  );

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="cover" className="max-w-4xl">
          <Modal.Dialog
            key={open ? (isEdit && log ? `edit-${String(log.id ?? '')}` : 'create') : 'closed'}
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑调用日志' : '创建调用日志'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改调用记录' : '记录一次 AI 调用与扣费信息'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-ai-invoke-log-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                {renderSelect(
                  'spaceId',
                  '空间',
                  spaces.map((s) => ({ id: s.id, label: s.name || s.id }))
                )}
                {renderSelect(
                  'userId',
                  '用户',
                  users.map((u) => ({
                    id: u.id,
                    label: u.nickname || u.username || u.id,
                  }))
                )}
                {renderSelect(
                  'providerId',
                  '供应商',
                  providers.map((p) => ({ id: p.id, label: p.name || p.code || p.id }))
                )}
                {renderSelect(
                  'modelId',
                  '模型',
                  models.map((m) => ({
                    id: m.id,
                    label: modelLabelMap.get(m.id) ?? m.id,
                  }))
                )}
                {renderSelect(
                  'ruleId',
                  '计费规则',
                  rules.map((r) => {
                    const modelLabel = r.modelId ? modelLabelMap.get(r.modelId) ?? r.modelId : '—';
                    const ver = r.version != null ? ` v${r.version}` : '';
                    const currency = r.currency
                      ? ` · ${AI_BILLING_CURRENCY_LABELS[r.currency as AiBillingCurrency] ?? r.currency}`
                      : '';
                    return { id: r.id, label: `${modelLabel}${ver}${currency}` };
                  })
                )}

                <Controller
                  control={form.control}
                  name="requestId"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.requestId}>
                      <Label className="text-sm font-medium">
                        请求 ID <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={100}
                        placeholder="全局唯一请求追踪 ID"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.requestId ? (
                        <FieldError>{errors.requestId.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <AiInvokeUsageItemsEditor
                  control={control}
                  errors={errors}
                  onRowsChange={syncAmountFromItems}
                />

                <Controller
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.amount}>
                      <Label className="text-sm font-medium">
                        总计 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        variant="secondary"
                        value={String(field.value ?? '')}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">
                        默认按使用明细小计求和；积分扣减由服务端按 amount × 100 自动计算
                      </Description>
                      {errors.amount ? <FieldError>{errors.amount.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.status}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        状态 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {AI_INVOKE_STATUSES.map((s) => (
                            <ListBox.Item key={s} id={s} textValue={AI_INVOKE_STATUS_LABELS[s]}>
                              {AI_INVOKE_STATUS_LABELS[s]}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.status ? <FieldError>{errors.status.message}</FieldError> : null}
                    </Select>
                  )}
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
